import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { formatLapTime, formatDelta } from '../utils/format';
import TrackThumbnail from './TrackThumbnail';

function fmtMs(ms) {
  if (!ms || ms <= 0) return '--:--.---';
  return formatLapTime(ms / 1000);
}

function fmtMsShort(ms) {
  if (!ms || ms <= 0) return '--:--';
  const s = ms / 1000;
  return Math.floor(s / 60) + ':' + (s % 60).toFixed(1).padStart(4, '0');
}

const classBadgeColor = {
  SRF: { bg: 'rgba(39,174,96,0.15)', color: '#27ae60' },
  GT3: { bg: 'rgba(231,76,60,0.15)', color: '#e74c3c' },
  LMP2: { bg: 'rgba(155,89,182,0.15)', color: '#9b59b6' },
  'MX-5': { bg: 'rgba(243,156,18,0.15)', color: '#f39c12' },
  GTE: { bg: 'rgba(46,134,193,0.15)', color: '#2e86c1' },
};

const statusColors = {
  'on-track': '#27ae60',
  pit: '#f39c12',
  'off-track': '#e74c3c',
  finished: '#888',
};

const statusLabels = {
  'on-track': 'On Track',
  pit: 'In Pit',
  'off-track': 'Off Track',
  finished: 'Finished',
};

const S = {
  headerBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, padding: '14px 20px',
    background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  headerTitle: {
    fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700,
    letterSpacing: 2, color: 'var(--color-secondary)', textTransform: 'uppercase',
  },
  liveDot: (color) => ({
    width: 10, height: 10, borderRadius: '50%', background: color,
    boxShadow: `0 0 8px ${color}`,
    animation: 'livePulse 1.5s ease-in-out infinite',
  }),
  headerStats: { fontSize: 13, color: 'var(--color-text-dim)', display: 'flex', gap: 16, alignItems: 'center' },
  headerStatNum: { fontFamily: 'var(--font-data)', fontWeight: 600, color: 'var(--color-text)' },
  connBadge: (ok) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px',
    background: ok ? 'rgba(39,174,96,0.12)' : 'rgba(231,76,60,0.12)',
    border: `1px solid ${ok ? 'rgba(39,174,96,0.3)' : 'rgba(231,76,60,0.3)'}`,
    borderRadius: 12, fontFamily: 'var(--font-heading)', fontSize: 9, fontWeight: 700,
    letterSpacing: 1.5, color: ok ? '#27ae60' : '#e74c3c', textTransform: 'uppercase',
  }),
  trackCard: {
    background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10,
    marginBottom: 24, overflow: 'hidden',
  },
  trackHeader: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px',
    borderBottom: '1px solid var(--color-border)',
    background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-surface-light) 100%)',
  },
  trackThumb: {
    width: 56, height: 40, borderRadius: 4, objectFit: 'cover',
    background: '#0D1B2A', flexShrink: 0,
  },
  trackName: {
    fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700,
    letterSpacing: 1, color: 'var(--color-text)',
  },
  trackMeta: { fontSize: 12, color: 'var(--color-text-dim)', marginTop: 2 },
  trackBest: {
    marginLeft: 'auto', textAlign: 'right', flexShrink: 0,
  },
  trackBestLabel: {
    fontFamily: 'var(--font-heading)', fontSize: 9, fontWeight: 500,
    letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-text-dim)',
  },
  trackBestTime: {
    fontFamily: 'var(--font-data)', fontSize: 18, fontWeight: 500, color: 'var(--color-success)',
  },
  liveBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
    background: 'rgba(39,174,96,0.15)', border: '1px solid rgba(39,174,96,0.3)',
    borderRadius: 12, fontFamily: 'var(--font-heading)', fontSize: 9, fontWeight: 700,
    letterSpacing: 1.5, color: '#27ae60', textTransform: 'uppercase',
  },
  th: {
    fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
    textTransform: 'uppercase', color: 'var(--color-text-dim)', padding: '10px 12px',
    background: 'var(--color-surface-light)', borderBottom: '1px solid var(--color-border)',
    textAlign: 'left',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--color-border)' },
  posCell: (i) => ({
    textAlign: 'center', fontFamily: 'var(--font-data)', fontSize: 16, fontWeight: 700,
    color: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'var(--color-text-dim)',
    padding: '10px 8px', width: 44,
  }),
  driverName: { fontWeight: 600, fontSize: 13, color: 'var(--color-text)' },
  driverTeam: { fontSize: 11, color: 'var(--color-text-dim)', marginTop: 1 },
  carBadge: {
    display: 'inline-block', padding: '3px 10px', background: 'rgba(46,134,193,0.12)',
    borderRadius: 12, fontSize: 11, fontWeight: 600, color: 'var(--color-accent)', whiteSpace: 'nowrap',
  },
  classBadge: (cls) => {
    const c = classBadgeColor[cls] || { bg: 'rgba(46,134,193,0.12)', color: 'var(--color-accent)' };
    return {
      display: 'inline-block', padding: '3px 10px', background: c.bg,
      borderRadius: 12, fontSize: 11, fontWeight: 600, color: c.color,
    };
  },
  statusBadge: (status) => {
    const c = statusColors[status] || '#888';
    return {
      display: 'inline-block', padding: '3px 10px', borderRadius: 12,
      fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-heading)',
      letterSpacing: 1, textTransform: 'uppercase',
      background: `${c}22`, color: c,
    };
  },
  lapTimeCell: { fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 500 },
  gapCell: (v) => ({
    fontFamily: 'var(--font-data)', fontSize: 12,
    color: v > 0 ? 'var(--color-danger)' : 'var(--color-text-dim)',
  }),
  progressBar: { width: 50, height: 3, background: 'var(--color-border)', borderRadius: 2, marginTop: 4, overflow: 'hidden' },
  progressFill: (pct) => ({ width: `${pct}%`, height: '100%', background: 'var(--color-accent)', borderRadius: 2, transition: 'width 0.8s linear' }),
  rowBorder: (status) => ({
    borderLeft: `3px solid ${statusColors[status] || '#888'}`,
  }),
  emptyState: {
    textAlign: 'center', padding: '64px 24px',
    background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10,
  },
  emptyIcon: {
    fontSize: 48, marginBottom: 16, opacity: 0.3,
  },
  emptyTitle: {
    fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, letterSpacing: 2,
    color: 'var(--color-text-dim)', textTransform: 'uppercase', marginBottom: 8,
  },
  emptyDesc: { fontSize: 13, color: 'var(--color-text-dim)', lineHeight: 1.6 },
  activityHeader: {
    fontFamily: 'var(--font-heading)', fontSize: 12, fontWeight: 700,
    letterSpacing: 1.5, color: 'var(--color-secondary)', textTransform: 'uppercase', marginBottom: 12,
  },
  activityWrap: {
    background: 'var(--color-surface)', border: '1px solid var(--color-border)',
    borderRadius: 10, maxHeight: 320, overflow: 'auto',
  },
  activityItem: (isLast) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px',
    borderBottom: isLast ? 'none' : '1px solid var(--color-border)', fontSize: 13,
  }),
  activityTime: { fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--color-text-dim)', minWidth: 55 },
  activityPb: {
    display: 'inline-block', padding: '1px 6px', borderRadius: 4, marginLeft: 6,
    background: 'rgba(39,174,96,0.15)', color: '#27ae60',
    fontFamily: 'var(--font-heading)', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  },
};

// Inject keyframe animation via style tag
const pulseStyle = `
@keyframes livePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.85)} }
@keyframes lapFlash {
  0% { background: rgba(39,174,96,0.25); }
  100% { background: transparent; }
}
`;

export default function LiveTelemetryPanel() {
  const [drivers, setDrivers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [connected, setConnected] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [flashedEmails, setFlashedEmails] = useState(new Set());
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('rv_token');
    const socket = io({
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('snapshot', (data) => {
      setDrivers(Array.isArray(data) ? data : []);
    });

    socket.on('driver-update', (data) => {
      setDrivers(prev => {
        const idx = prev.findIndex(d => d.email === data.email);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = data;
          return next;
        }
        return [...prev, data];
      });
    });

    socket.on('driver-offline', ({ email }) => {
      setDrivers(prev => prev.filter(d => d.email !== email));
    });

    socket.on('lap-complete', (data) => {
      setActivity(prev => [{
        ...data,
        time: Date.now(),
      }, ...prev].slice(0, 50));
      // Flash the driver's row
      if (data.email) {
        setFlashedEmails(prev => new Set(prev).add(data.email));
        setTimeout(() => {
          setFlashedEmails(prev => {
            const next = new Set(prev);
            next.delete(data.email);
            return next;
          });
        }, 2000);
      }
    });

    return () => { socket.disconnect(); };
  }, []);

  // Refresh relative timestamps every 5s
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // Group drivers by track
  const groups = {};
  drivers.forEach(d => {
    if (!groups[d.trackId]) groups[d.trackId] = { trackId: d.trackId, trackName: d.trackName || d.trackId, trackLocation: d.trackLocation || '', drivers: [] };
    groups[d.trackId].drivers.push(d);
  });

  // Sort each group by best lap
  Object.values(groups).forEach(g => {
    g.drivers.sort((a, b) => {
      if (!a.bestLapMs) return 1;
      if (!b.bestLapMs) return -1;
      return a.bestLapMs - b.bestLapMs;
    });
  });

  const trackCount = Object.keys(groups).length;
  const onTrack = drivers.filter(d => d.status === 'on-track').length;
  const inPit = drivers.filter(d => d.status === 'pit').length;

  return (
    <div>
      <style>{pulseStyle}</style>

      {/* ── Header Bar ── */}
      <div style={S.headerBar}>
        <div style={S.headerLeft}>
          <div style={S.liveDot(connected ? '#27ae60' : '#e74c3c')} />
          <span style={S.headerTitle}>Live Timing</span>
        </div>
        {drivers.length > 0 && (
          <div style={S.headerStats}>
            <span><span style={S.headerStatNum}>{drivers.length}</span> driver{drivers.length !== 1 ? 's' : ''}</span>
            <span><span style={S.headerStatNum}>{trackCount}</span> track{trackCount !== 1 ? 's' : ''}</span>
            <span style={{ color: '#27ae60' }}>{onTrack} racing</span>
            {inPit > 0 && <span style={{ color: '#f39c12' }}>{inPit} pit</span>}
          </div>
        )}
        <div style={S.connBadge(connected)}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: connected ? '#27ae60' : '#e74c3c' }} />
          {connected ? 'Connected' : 'Disconnected'}
        </div>
      </div>

      {/* ── Track Groups ── */}
      {Object.entries(groups)
        .sort((a, b) => b[1].drivers.length - a[1].drivers.length)
        .map(([trackId, group]) => {
          const leader = group.drivers.find(d => d.bestLapMs > 0);
          const groupOnTrack = group.drivers.filter(d => d.status === 'on-track').length;
          const groupPit = group.drivers.filter(d => d.status === 'pit').length;

          return (
            <div key={trackId} style={S.trackCard}>
              {/* Track Header Card */}
              <div style={S.trackHeader}>
                <TrackThumbnail
                  trackId={trackId}
                  alt=""
                  style={S.trackThumb}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={S.trackName}>{group.trackName}</span>
                    <span style={S.liveBadge}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#27ae60', animation: 'livePulse 1.5s ease-in-out infinite' }} />
                      Live
                    </span>
                  </div>
                  <div style={S.trackMeta}>
                    {group.trackLocation && <span>{group.trackLocation} &middot; </span>}
                    {group.drivers.length} driver{group.drivers.length > 1 ? 's' : ''}
                    {groupOnTrack > 0 && <span> &middot; {groupOnTrack} racing</span>}
                    {groupPit > 0 && <span> &middot; {groupPit} pit</span>}
                  </div>
                </div>
                {leader && (
                  <div style={S.trackBest}>
                    <div style={S.trackBestLabel}>Session Best</div>
                    <div style={S.trackBestTime}>{fmtMs(leader.bestLapMs)}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 1 }}>{leader.driver}</div>
                  </div>
                )}
              </div>

              {/* Timing Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      { label: 'Pos', align: 'center', w: 44 },
                      { label: 'Driver' },
                      { label: 'Car' },
                      { label: 'Class', w: 70 },
                      { label: 'Laps', w: 55 },
                      { label: 'Last Lap', w: 110 },
                      { label: 'Best Lap', w: 110 },
                      { label: 'Gap', w: 80 },
                      { label: 'Speed', w: 70 },
                      { label: 'Status', w: 90 },
                    ].map(h => (
                      <th key={h.label} style={{ ...S.th, textAlign: h.align || 'left', width: h.w }}>{h.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.drivers.map((d, i) => {
                    const leaderBest = leader?.bestLapMs || 0;
                    const gapSec = d.bestLapMs && leaderBest && i > 0 ? (d.bestLapMs - leaderBest) / 1000 : 0;
                    const progress = d.status === 'on-track' && d.lapTimeMs && d.bestLapMs
                      ? Math.min(100, (d.lapTimeMs / d.bestLapMs) * 100) : 0;

                    return (
                      <tr key={d.email} style={{
                        ...S.rowBorder(d.status),
                        ...(flashedEmails.has(d.email) ? { animation: 'lapFlash 2s ease-out' } : {}),
                      }}>
                        <td style={S.posCell(i)}>{i + 1}</td>
                        <td style={S.td}>
                          <div style={S.driverName}>{d.driver || d.email}</div>
                          {d.team && <div style={S.driverTeam}>{d.team}</div>}
                        </td>
                        <td style={S.td}><span style={S.carBadge}>{d.car}</span></td>
                        <td style={S.td}>{d.racingClass && <span style={S.classBadge(d.racingClass)}>{d.racingClass}</span>}</td>
                        <td style={{ ...S.td, fontFamily: 'var(--font-data)', fontSize: 13 }}>{d.lapNum}</td>
                        <td style={S.td}>
                          <div style={{ ...S.lapTimeCell, color: 'var(--color-text)' }}>{fmtMs(d.lastLapMs)}</div>
                          {d.status === 'on-track' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--color-accent)' }}>{fmtMsShort(d.lapTimeMs)}</span>
                              <div style={S.progressBar}><div style={S.progressFill(progress)} /></div>
                            </div>
                          )}
                        </td>
                        <td style={{ ...S.td, ...S.lapTimeCell, color: 'var(--color-success)' }}>{fmtMs(d.bestLapMs)}</td>
                        <td style={{ ...S.td, ...S.gapCell(gapSec) }}>
                          {gapSec > 0 ? formatDelta(gapSec) : '--'}
                        </td>
                        <td style={{ ...S.td, fontFamily: 'var(--font-data)', fontSize: 13 }}>
                          {d.status === 'on-track' ? Math.round(d.speed || 0) : '--'}
                        </td>
                        <td style={S.td}><span style={S.statusBadge(d.status)}>{statusLabels[d.status] || d.status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

      {/* ── Empty State ── */}
      {drivers.length === 0 && (
        <div style={S.emptyState}>
          <div style={S.emptyIcon}>&#127937;</div>
          <div style={S.emptyTitle}>No Drivers on Track</div>
          <div style={S.emptyDesc}>
            Drivers will appear here when they connect via<br />
            RaceVoice App or RaceVoiceSim
          </div>
        </div>
      )}

      {/* ── Activity Feed ── */}
      {activity.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={S.activityHeader}>Recent Laps</div>
          <div style={S.activityWrap}>
            {activity.slice(0, 30).map((a, i) => {
              const ago = Math.round((now - a.time) / 1000);
              const agoText = ago < 60 ? ago + 's ago' : Math.round(ago / 60) + 'm ago';
              const isPb = a.lapTimeMs && a.bestLapMs && a.lapTimeMs <= a.bestLapMs;
              return (
                <div key={i} style={S.activityItem(i === Math.min(29, activity.length - 1))}>
                  <span style={S.activityTime}>{agoText}</span>
                  <span style={{ flex: 1 }}>
                    <strong style={{ color: 'var(--color-accent)' }}>{a.driver || a.email}</strong>
                    {' '}lap {a.lapNum} at {a.trackName}
                  </span>
                  <span style={{ fontFamily: 'var(--font-data)', fontSize: 13, fontWeight: 500, color: isPb ? 'var(--color-success)' : 'var(--color-text)' }}>
                    {fmtMs(a.lapTimeMs)}
                    {isPb && <span style={S.activityPb}>PB</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
